import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';

/**
 * Weekly Areas of Concern one-pager (schedule-update email body).
 * Diff vs previous week: new = red, expired = strikethrough.
 */
const AreasOfConcernOnePager = ({ jobNumber, jobName, onBack }) => {
  const { instance, accounts } = useMsal();
  const [weeks, setWeeks] = useState([]);
  const [weekKey, setWeekKey] = useState('');
  const [week, setWeek] = useState(null);
  const [edit, setEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [present, setPresent] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showDiff, setShowDiff] = useState(true);
  const [newWeekKey, setNewWeekKey] = useState('');
  const [extraIntel, setExtraIntel] = useState('');
  const [mailboxNote, setMailboxNote] = useState('');
  const [notebookHints, setNotebookHints] = useState([]);
  const [changeLogHints, setChangeLogHints] = useState([]);
  const [rfiHints, setRfiHints] = useState([]);
  const [sharePointHints, setSharePointHints] = useState([]);
  const [p6Hints, setP6Hints] = useState([]);
  const [missingWeeks, setMissingWeeks] = useState([]);
  const [p6Current, setP6Current] = useState(null);
  const [p6ActivitySync, setP6ActivitySync] = useState(null);
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [members, setMembers] = useState([]);
  const [emailTo, setEmailTo] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const fileInputRef = useRef(null);
  const pageRef = useRef(null);

  const applyDraftHints = (weekBody, draftPayload) => {
    setNotebookHints(
      weekBody?.suggestedFromNotebook
        || draftPayload?.notebook?.notes?.map((n) => n.memo).filter(Boolean)
        || []
    );
    setChangeLogHints(
      weekBody?.suggestedFromChangeLog
        || draftPayload?.changeLog?.suggestions
        || []
    );
    setRfiHints(
      weekBody?.suggestedFromRfis
        || draftPayload?.rfis?.suggestions
        || []
    );
    setSharePointHints(
      weekBody?.suggestedFromSharePoint
        || draftPayload?.sharePoint?.hints
        || []
    );
    setP6Hints(
      weekBody?.suggestedFromP6
        || draftPayload?.p6Activities?.suggestions
        || []
    );
  };

  const load = useCallback(async (preferredWeek) => {
    if (!jobNumber) return;
    try {
      setLoading(true);
      setError(null);
      const qs = preferredWeek ? `?week=${encodeURIComponent(preferredWeek)}` : '';
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/${jobNumber}/areas-of-concern${qs}`
      );
      const data = res.data.data;
      setWeeks(data.weeks || []);
      setWeek(data.week || null);
      setEdit(data.week ? cloneWeek(data.week) : null);
      setWeekKey(data.selectedWeek || preferredWeek || '');
      setExtraIntel(data.week?.body?.extraIntel || '');
      applyDraftHints(data.week?.body || null, null);
      setMailboxNote(data.mailboxAccess?.reason || '');
      setMissingWeeks(data.likelyMissingWeeks || []);
      setP6Current(data.p6Current || null);
      setP6ActivitySync(data.p6ActivitySync || null);
      setProfile(data.profile || null);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || 'Failed to load Areas of Concern');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!present) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPresent(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present]);

  const save = async (status) => {
    if (!edit || !weekKey) return;
    try {
      setSaving(true);
      const payload = {
        title: edit.title,
        status: status || edit.status,
        summary: edit.summary,
        greeting: edit.greeting || edit.body?.greeting,
        asOfLabel: edit.asOfLabel || edit.body?.asOfLabel,
        body: {
          ...edit.body,
          extraIntel,
          suggestedFromNotebook: notebookHints,
          suggestedFromChangeLog: changeLogHints,
          suggestedFromRfis: rfiHints,
          suggestedFromSharePoint: sharePointHints,
          suggestedFromP6: p6Hints,
        },
        sourceNote: edit.sourceNote,
        p6ProjShortName: edit.p6ProjShortName,
      };
      const res = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}`,
        payload
      );
      setWeek(res.data.data);
      setEdit(cloneWeek(res.data.data));
      setEditing(false);
      setWeeks((ws) => ws.map((w) => (
        w.weekKey === weekKey
          ? { ...w, status: res.data.data.status, title: res.data.data.title }
          : w
      )));
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const createWeek = async ({ draftFromPrev = true, weekKeyOverride } = {}) => {
    const key = String(weekKeyOverride || newWeekKey || '').replace(/-/g, '');
    if (!/^\d{8}$/.test(key)) {
      alert('Enter week as YYYYMMDD (e.g. 20260802)');
      return;
    }
    try {
      setSaving(true);
      if (draftFromPrev) {
        const tipName = (p6Current?.available && p6Current.weekKey === key)
          ? p6Current.projShortName
          : null;
        const res = await authenticatedApiCall(
          instance, accounts, 'POST',
          `/ga-dashboards/${jobNumber}/areas-of-concern/${key}/draft`,
          {
            extraIntel,
            p6ProjShortName: tipName || undefined,
            save: true,
          }
        );
        const data = res.data.data;
        applyDraftHints(data.week?.body || null, data);
        if (data.mailboxAccess?.reason) setMailboxNote(data.mailboxAccess.reason);
      } else {
        await authenticatedApiCall(
          instance, accounts, 'PUT',
          `/ga-dashboards/${jobNumber}/areas-of-concern/${key}`,
          {
            title: 'Areas of Concern',
            status: 'draft',
            summary: '',
            body: {
              template: profile?.template,
              sections: (profile?.sections || defaultSections()).map((title) => (
                typeof title === 'string' ? { title, bullets: [] } : title
              )),
              recordedDelays: [],
              outstandingNccs: [],
              sourceRefs: [],
              extraIntel,
              closingAsk: profile?.closingAsk || '',
              scheduleAttachments: [],
            },
            sourceNote: `New week ${key}`,
            greeting: profile?.greeting,
            p6ProjShortName: undefined,
          }
        );
      }
      setNewWeekKey('');
      // Stay in view mode so Diff colors (red / strikethrough) are visible on the draft.
      setEditing(false);
      setShowDiff(true);
      await load(key);
    } catch (e) {
      alert(e.response?.data?.error || 'Could not create week');
    } finally {
      setSaving(false);
    }
  };

  const refreshDraft = async () => {
    if (!weekKey) return;
    try {
      setSaving(true);
      const tipName = (p6Current?.available && p6Current.weekKey === weekKey)
        ? p6Current.projShortName
        : null;
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}/draft`,
        {
          extraIntel,
          p6ProjShortName: tipName || week?.p6ProjShortName || undefined,
          save: true,
          refreshCurrent: true,
        },
        { timeout: 60000 }
      );
      const data = res.data?.data;
      if (!data?.week) throw new Error(res.data?.error || 'Draft refresh returned no week');
      applyDraftHints(data.week?.body || null, data);
      if (data.sourceErrors?.length) {
        setMailboxNote(data.sourceErrors.join(' · '));
      } else if (data.mailboxAccess?.reason) {
        setMailboxNote(data.mailboxAccess.reason);
      }
      // View + Diff colors on so new/removed bullets show red / strikethrough.
      setEditing(false);
      setShowDiff(true);
      await load(weekKey);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Draft refresh failed';
      alert(/timeout|ETIMEOUT|Failed to connect/i.test(msg)
        ? `Draft refresh failed — a live source (P6 / SharePoint) may be unreachable. (${msg})`
        : msg);
    } finally {
      setSaving(false);
    }
  };

  const uploadAttachment = async (file) => {
    if (!file || !weekKey) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}/attachments`,
        formData
      );
      setWeek(res.data.data);
      setEdit(cloneWeek(res.data.data));
    } catch (e) {
      alert(e.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeAttachment = async (id) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      setUploading(true);
      const res = await authenticatedApiCall(
        instance, accounts, 'DELETE',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}/attachments/${id}`
      );
      setWeek(res.data.data);
      setEdit(cloneWeek(res.data.data));
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}/attachments/${att.id}`,
        null,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName || 'attachment';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.data?.error || 'Download failed');
    }
  };

  const copyEmail = async () => {
    const text = week?.emailBody || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      alert('Email body copied');
    } catch {
      alert('Could not copy — select text manually');
    }
  };

  const openEmailModal = async () => {
    if (!weekKey || !week) return;
    setEmailResult(null);
    setShowEmail(true);
    setIncludeAttachments(true);
    setEmailSubject(
      `LGL Schedule — ${jobNumber} — Areas of Concern (as of ${week.asOfLabel || week.weekLabel || weekKey})`
    );
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/ga-dashboards/${jobNumber}/areas-of-concern/recipients`
      );
      const data = res.data?.data || {};
      setMembers(data.members || []);
      setEmailTo(data.defaults?.to || ['dmoore@laframboise.net']);
      setEmailCc(data.defaults?.cc || ['yrezaei@laframboise.net', 'bamiri@laframboise.net']);
    } catch (e) {
      setMembers([
        { email: 'dmoore@laframboise.net', name: 'Darrell Moore' },
        { email: 'yrezaei@laframboise.net', name: 'Yousef Rezaei' },
        { email: 'bamiri@laframboise.net', name: 'Behzad Amiri' },
      ]);
      setEmailTo(['dmoore@laframboise.net']);
      setEmailCc(['yrezaei@laframboise.net', 'bamiri@laframboise.net']);
      setEmailResult({
        type: 'error',
        message: e.response?.data?.error || 'Could not load project members — defaults loaded',
      });
    }
  };

  const addRecipient = (list, setList, email) => {
    const e = String(email || '').trim().toLowerCase();
    if (!e || !e.includes('@')) return;
    setList((prev) => (prev.includes(e) ? prev : [...prev, e]));
  };

  const removeRecipient = (list, setList, email) => {
    setList((prev) => prev.filter((x) => x !== email));
  };

  const memberLabel = (email) => {
    const m = members.find((x) => String(x.email).toLowerCase() === String(email).toLowerCase());
    return m ? `${m.name} <${m.email}>` : email;
  };

  const sendEmail = async () => {
    if (!weekKey) return;
    if (!emailTo.length) {
      setEmailResult({ type: 'error', message: 'Pick at least one To recipient' });
      return;
    }
    try {
      setEmailSending(true);
      setEmailResult(null);
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/ga-dashboards/${jobNumber}/areas-of-concern/${weekKey}/send-email`,
        {
          to: emailTo,
          cc: emailCc,
          subject: emailSubject,
          includeAttachments,
          includeReviewAsk: true,
        }
      );
      const data = res.data?.data;
      if (data?.skipped) {
        setEmailResult({ type: 'error', message: 'Send skipped — recipients unsubscribed' });
        return;
      }
      setEmailResult({
        type: 'success',
        message: `Sent to ${data.sentTo?.join(', ')}`
          + (data.cc?.length ? ` (CC ${data.cc.join(', ')})` : '')
          + (data.attachmentCount ? ` · ${data.attachmentCount} attachment(s)` : ''),
      });
      // Brief success flash, then close — don't leave the modal stuck open
      window.setTimeout(() => {
        setShowEmail(false);
        setEmailResult(null);
      }, 800);
    } catch (e) {
      setEmailResult({
        type: 'error',
        message: e.response?.data?.error || e.message || 'Send failed',
      });
    } finally {
      setEmailSending(false);
    }
  };

  const exportPng = async () => {
    const node = pageRef.current;
    if (!node) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff' });
      const a = document.createElement('a');
      a.download = `${jobNumber || 'job'}-Areas-of-Concern-${weekKey || 'draft'}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } catch (e) {
      alert('PNG export failed');
      console.error(e);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading Areas of Concern…</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '1rem 1.5rem' }}>
        <button onClick={onBack} style={btnSecondary}>← Back</button>
        <div style={{ marginTop: 16, padding: 16, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, color: '#b91c1c' }}>{error}</div>
      </div>
    );
  }

  const view = editing ? edit : week;
  const displayBody = (!editing && showDiff && week?.diff) ? week.diff : (view?.body || { sections: [], recordedDelays: [] });
  const comparedTo = week?.diff?.comparedToWeekKey;

  return (
    <div style={{ padding: present ? 12 : '0.75rem 1rem 1.25rem', background: present ? '#0f172a' : 'transparent', minHeight: present ? '100vh' : undefined }}>
      {!present && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={btnSecondary}>← Back</button>
          <h2 style={{ margin: 0, fontSize: 20 }}>Areas of Concern</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            Week
            <select
              value={weekKey}
              onChange={(e) => { setWeekKey(e.target.value); setEditing(false); load(e.target.value); }}
              style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
            >
              {weeks.map((w) => (
                <option key={w.weekKey} value={w.weekKey}>
                  {w.weekLabel} · {w.status}
                  {p6Current?.available && p6Current.weekKey === w.weekKey ? ' · P6 Current' : ''}
                </option>
              ))}
            </select>
          </label>
          {p6Current?.available && weekKey === p6Current.weekKey && (
            <span style={{ fontSize: 11, color: '#0f766e', fontWeight: 600 }}>
              from P6 Current data date ({p6Current.dataDate}
              {p6Current.projShortName ? ` · ${p6Current.projShortName}` : ''})
            </span>
          )}
          {p6ActivitySync?.skipped === 'user-edited' && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#334155' }}>
              saved draft kept — Refresh to re-read P6
            </span>
          )}
          {p6ActivitySync?.source && (
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: p6ActivitySync.source === 'p6-current' ? '#0f766e' : '#b45309',
            }}
            >
              dates from {p6ActivitySync.projShortName || p6ActivitySync.source}
              {p6ActivitySync.source !== 'p6-current' ? ' (not live Current)' : ''}
              {p6ActivitySync.error ? ` · ${p6ActivitySync.error}` : ''}
            </span>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={showDiff} onChange={(e) => setShowDiff(e.target.checked)} />
            Diff colors
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              value={newWeekKey}
              onChange={(e) => setNewWeekKey(e.target.value)}
              placeholder="New YYYYMMDD"
              style={{ width: 120, padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 12 }}
            />
            <button onClick={() => createWeek({ draftFromPrev: true })} disabled={saving} style={btnPrimary}>
              + Draft from last week
            </button>
          </div>
          <button onClick={refreshDraft} disabled={saving || !weekKey} style={btnSecondary}>
            Refresh draft sources
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowHelp(true)} style={{ ...btnSecondary, padding: '7px 10px' }}>❓</button>
          {!editing ? (
            <button onClick={() => { setEdit(cloneWeek(week)); setEditing(true); }} style={btnSecondary} disabled={!week}>Edit</button>
          ) : (
            <>
              <button onClick={() => { setEditing(false); setEdit(cloneWeek(week)); }} style={btnSecondary}>Cancel</button>
              <button onClick={() => save('draft')} disabled={saving} style={btnSecondary}>{saving ? '…' : 'Save draft'}</button>
              <button onClick={() => save('published')} disabled={saving} style={btnPrimary}>{saving ? '…' : 'Publish'}</button>
            </>
          )}
          <button onClick={copyEmail} style={btnSecondary} disabled={!week?.emailBody}>Copy email</button>
          <button onClick={openEmailModal} style={btnPrimary} disabled={!week?.emailBody}>✉ Email</button>
          <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
          <button onClick={() => setPresent(true)} style={btnPrimary}>Present</button>
        </div>
      )}

      {present && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
          <button onClick={exportPng} style={btnSecondary}>⬇ PNG</button>
          <button onClick={() => setPresent(false)} style={btnPrimary}>Exit (Esc)</button>
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 900, maxWidth: '94vw', maxHeight: '84vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <strong>❓ Areas of Concern Help</strong>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="areas-of-concern-help" title="Areas of Concern Help" accent="#0f766e" dark />
                <button onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 18, fontSize: 14, lineHeight: 1.55, color: '#334155', overflow: 'auto', flex: 1, minHeight: 0 }}>
              <HelpWithVideoTour guideId="areas-of-concern-help" title="Areas of Concern Help" accent="#0f766e">
              <p>Weekly schedule-update <strong>email body</strong> with history — on every project, not just Goodyear.</p>
              <p><strong>75998</strong> uses Engineering / Procurement / Construction / Interfaces / Change Management. <strong>71940</strong> and new jobs use <strong>Assumptions</strong> + <strong>Needs</strong> with nested dated lists (foundation dates, deliveries, mechanical installs).</p>
              <p><span style={{ color: '#dc2626', fontWeight: 700 }}>Red</span> = new vs previous week. <span style={{ textDecoration: 'line-through' }}>Strikethrough</span> = dropped since last week. Indent a line with two spaces to nest it under the bullet above.</p>
              <p><strong>+ Draft from last week</strong> copies the prior body into a new week. <strong>Save draft</strong> keeps your words — page load will not overwrite them with P6. <strong>Refresh draft sources</strong> is the button that re-reads live P6 on this week.</p>
              <p>On 75998, draft/refresh also pulls P6 notebooks, the SharePoint Control Chart, RFIs, and Additional Scope hours. On <strong>every job</strong> it reads the live P6 schedule for activities whose <strong>Resp. Party</strong> is not LGL — anywhere in the WBS. Matching dated bullets get a date if they were TBD, or update when P6 slipped <em>later</em>. An emailed access / curing date is <strong>never moved backward</strong> to an earlier completed pour.</p>
              <p>Check the Extra intel <strong>P6 3rd-party</strong> panel for activity IDs and “kept vs applied” before you send. Unmatched third-party work stays in that panel — it is not dumped onto the email.</p>
              <p><strong>✉ Email</strong> sends from the portal (defaults: To Darrell, CC you + Behzad). Recipients are project members.</p>
              <p>Outlook mailbox read is <strong>not</strong> enabled yet — paste priority emails into Extra intel.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}

      {showEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 560, maxWidth: '96vw', overflow: 'hidden', boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}>
            <div style={{ padding: '14px 18px', background: '#0f766e', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>✉ Send Areas of Concern</strong>
              <button onClick={() => setShowEmail(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, color: '#334155' }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                From <strong>portal@laframboise.net</strong> · week <strong>{weekKey}</strong>
                {' · '}
                {members.length} project member{members.length === 1 ? '' : 's'} available for To / CC
              </div>

              <RecipientPicker
                label="To"
                selected={emailTo}
                members={members}
                onAdd={(email) => addRecipient(emailTo, setEmailTo, email)}
                onRemove={(email) => removeRecipient(emailTo, setEmailTo, email)}
                memberLabel={memberLabel}
              />
              <RecipientPicker
                label="CC"
                selected={emailCc}
                members={members}
                onAdd={(email) => addRecipient(emailCc, setEmailCc, email)}
                onRemove={(email) => removeRecipient(emailCc, setEmailCc, email)}
                memberLabel={memberLabel}
              />

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 12 }}>Subject</span>
                <input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <input
                  type="checkbox"
                  checked={includeAttachments}
                  onChange={(e) => setIncludeAttachments(e.target.checked)}
                />
                Include week attachments ({week?.body?.attachments?.length || 0})
              </label>

              {emailResult && (
                <div style={{
                  padding: 10, borderRadius: 8, fontSize: 12,
                  background: emailResult.type === 'success' ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${emailResult.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
                  color: emailResult.type === 'success' ? '#065f46' : '#b91c1c',
                }}
                >
                  {emailResult.message}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                <button type="button" onClick={() => setShowEmail(false)} style={btnSecondary}>Cancel</button>
                <button
                  type="button"
                  onClick={sendEmail}
                  disabled={emailSending || !emailTo.length}
                  style={btnPrimary}
                >
                  {emailSending ? 'Sending…' : 'Send email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!present && (
        <div style={{
          maxWidth: 960, margin: '0 auto 12px', padding: 12,
          background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10,
        }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, color: '#9a3412', marginBottom: 6 }}>
            Extra intel / priority list (not in SharePoint or P6)
          </div>
          <textarea
            value={extraIntel}
            onChange={(e) => setExtraIntel(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            rows={4}
            placeholder="Paste client priority list, email scraps, verbal updates…"
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #fdba74', fontSize: 12, boxSizing: 'border-box' }}
          />
          {mailboxNote && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#9a3412' }}>{mailboxNote}</div>
          )}
          {[
            { title: 'P6 3rd-party (Resp. Party ≠ LGL) — activity IDs / kept vs applied dates', items: p6Hints },
            { title: 'P6 notebook suggestions (★ = new dated / auto-merged concern)', items: notebookHints },
            { title: 'Control Chart xlsx / Change Log', items: changeLogHints },
            { title: 'RFI Log (Open / In Review)', items: rfiHints },
            { title: 'SharePoint RFI / related folders', items: sharePointHints },
          ].map((block) => (
            !!block.items.length && (
              <details key={block.title} style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#9a3412' }}>
                  {block.title} ({block.items.length})
                </summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 11, color: '#7c2d12' }}>
                  {block.items.map((h, i) => <li key={i} style={{ marginBottom: 3 }}>{h}</li>)}
                </ul>
              </details>
            )
          ))}
          <div style={{ marginTop: 8, fontSize: 11, color: '#64748b' }}>
            Legend: <span style={{ color: '#dc2626', fontWeight: 700 }}>new / changed</span>
            {' · '}
            <span style={{ textDecoration: 'line-through', color: '#64748b' }}>expired from last week</span>
            {comparedTo ? ` · compared to ${comparedTo}` : ''}
          </div>
        </div>
      )}

      {!present && weekKey && (
        <div style={{
          maxWidth: 960, margin: '0 auto 12px', padding: 12,
          background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10,
        }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ fontWeight: 800, fontSize: 13, color: '#334155' }}>
              Attachments (Word / Excel / email .msg / PDF / …)
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".doc,.docx,.xls,.xlsx,.msg,.eml,.pdf,.txt,.png,.jpg,.jpeg,.zip"
              style={{ display: 'none' }}
              onChange={(e) => uploadAttachment(e.target.files?.[0])}
            />
            <button
              type="button"
              disabled={uploading || !weekKey}
              onClick={() => fileInputRef.current?.click()}
              style={btnSecondary}
            >
              {uploading ? 'Uploading…' : '+ Add file'}
            </button>
          </div>
          {(view?.body?.attachments || []).length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>No attachments for this week yet.</div>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#0f172a' }}>
              {(view.body.attachments || []).map((att) => (
                <li key={att.id} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600 }}>{att.fileName}</span>
                  <span style={{ color: '#94a3b8' }}>
                    {att.size ? `${Math.round(att.size / 1024)} KB` : ''}
                    {att.uploadedBy ? ` · ${att.uploadedBy}` : ''}
                  </span>
                  <button type="button" onClick={() => downloadAttachment(att)} style={{ ...btnSecondary, padding: '3px 8px', fontSize: 11 }}>Download</button>
                  <button type="button" onClick={() => removeAttachment(att.id)} style={{ ...btnSecondary, padding: '3px 8px', fontSize: 11, color: '#b91c1c' }}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!present && missingWeeks.length > 0 && (
        <details style={{ maxWidth: 960, margin: '0 auto 12px', fontSize: 12, color: '#64748b' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Possible missing weeks (not in your paste)</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {missingWeeks.map((m) => (
              <li key={m.weekKey} style={{ marginBottom: 3 }}>
                <strong>{m.weekKey}</strong> — {m.reason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {!view ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '2px dashed #fdba74', borderRadius: 12, maxWidth: 640, margin: '24px auto' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', marginBottom: 8 }}>No week yet</div>
          <p style={{ fontSize: 13, lineHeight: 1.45, margin: '0 0 12px' }}>
            Enter a <strong>YYYYMMDD</strong> week (usually the P6 data date) and create the first schedule-update email.
            {p6Current?.available ? ` P6 Current is ${p6Current.weekKey}.` : ''}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <input
              value={newWeekKey}
              onChange={(e) => setNewWeekKey(e.target.value)}
              placeholder={p6Current?.weekKey || '20260809'}
              style={{ width: 140, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
            <button
              onClick={() => createWeek({
                draftFromPrev: true,
                weekKeyOverride: newWeekKey || p6Current?.weekKey,
              })}
              disabled={saving}
              style={btnPrimary}
            >
              {saving ? '…' : 'Create first week'}
            </button>
          </div>
        </div>
      ) : (
        <div
          ref={pageRef}
          style={{
            background: '#fff',
            border: '2px solid #0f172a',
            padding: '18px 22px 22px',
            maxWidth: 960,
            margin: '0 auto',
            boxShadow: present ? '0 8px 32px rgba(0,0,0,0.35)' : 'none',
            fontFamily: 'Calibri, Segoe UI, Arial, sans-serif',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 12, borderBottom: '2px solid #0f172a', paddingBottom: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>
              {profile?.pageTitle || (jobName ? `${jobName} — Areas of Concern` : 'Areas of Concern')}
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
              Week of {view.weekLabel || weekKey}
              {view.status === 'draft' ? ' · DRAFT' : ''}
              {view.p6ProjShortName ? ` · ${view.p6ProjShortName}` : ''}
            </div>
          </div>

          {editing ? (
            <textarea
              value={edit.summary}
              onChange={(e) => setEdit({ ...edit, summary: e.target.value })}
              onKeyDown={(e) => e.stopPropagation()}
              rows={2}
              placeholder="Please see the attached updated schedule as of …"
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, marginBottom: 12, boxSizing: 'border-box' }}
            />
          ) : (
            <p style={{ fontSize: 13, lineHeight: 1.5, color: '#0f172a', marginTop: 0, marginBottom: 12 }}>
              {(view.greeting || profile?.greeting || 'Hi,')}
              <br />
              {view.summary}
            </p>
          )}

          {profile?.heading ? (
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>{profile.heading}</div>
          ) : null}

          {(displayBody.sections || []).map((sec, si) => (
            <SectionBlock
              key={`${sec.title}-${si}`}
              section={sec}
              editing={editing}
              showDiff={showDiff && !editing}
              onChange={(next) => {
                const sections = [...edit.body.sections];
                sections[si] = { title: next.title, bullets: next.bullets };
                setEdit({ ...edit, body: { ...edit.body, sections } });
              }}
            />
          ))}

          {editing && profile?.template !== 'areas-of-concern' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>Closing ask</div>
              <input
                value={edit.body.closingAsk || ''}
                onChange={(e) => setEdit({ ...edit, body: { ...edit.body, closingAsk: e.target.value } })}
                placeholder="Please also provide the Master Schedule…"
                style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box' }}
              />
            </div>
          )}
          {!editing && (view.body?.closingAsk || displayBody.closingAsk) && (
            <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginTop: 12, marginBottom: 8 }}>
              {view.body?.closingAsk || displayBody.closingAsk}
            </p>
          )}

          {((editing && profile?.template === 'needs-assumptions')
            || (displayBody.scheduleAttachments || []).length
            || (view.body?.scheduleAttachments || []).length) && (
          <BulletList
            title="Attachments"
            items={displayBody.scheduleAttachments || view.body?.scheduleAttachments || []}
            editing={editing}
            showDiff={showDiff && !editing}
            onChange={(scheduleAttachments) => setEdit({
              ...edit,
              body: { ...edit.body, scheduleAttachments: scheduleAttachments.map(bulletPlain) },
            })}
          />
          )}

          {(profile?.hasRecordedDelays !== false) && (
          <BulletList
            title="Recorded Delays"
            items={displayBody.recordedDelays || []}
            editing={editing}
            showDiff={showDiff && !editing}
            onChange={(recordedDelays) => setEdit({
              ...edit,
              body: { ...edit.body, recordedDelays: recordedDelays.map(bulletPlain) },
            })}
          />
          )}

          {editing && (
            <BulletList
              title="Outstanding NCCs (optional helper)"
              items={edit.body.outstandingNccs || []}
              editing
              showDiff={false}
              onChange={(outstandingNccs) => setEdit({
                ...edit,
                body: { ...edit.body, outstandingNccs },
              })}
            />
          )}

          {!editing && view.sourceNote && (
            <div style={{ marginTop: 12, fontSize: 10, color: '#94a3b8' }}>{view.sourceNote}</div>
          )}
        </div>
      )}
    </div>
  );
};

function bulletPlain(b) {
  if (typeof b === 'string') return b;
  return b?.text || '';
}

function bulletChildren(b) {
  if (!b || typeof b !== 'object') return [];
  return Array.isArray(b.children) ? b.children : [];
}

function bulletsToEditText(items) {
  const lines = [];
  for (const b of items || []) {
    const text = bulletPlain(b);
    if (!text) continue;
    lines.push(text);
    for (const child of bulletChildren(b)) {
      const ct = bulletPlain(child);
      if (ct) lines.push(`  ${ct}`);
    }
  }
  return lines.join('\n');
}

function editTextToBullets(text) {
  const out = [];
  for (const raw of String(text || '').split('\n')) {
    if (/^\s{2,}\S/.test(raw) || /^\t/.test(raw)) {
      const child = raw.trim();
      if (!child) continue;
      if (!out.length) {
        out.push(child);
        continue;
      }
      const last = out[out.length - 1];
      if (typeof last === 'string') {
        out[out.length - 1] = { text: last, children: [child] };
      } else {
        last.children = [...(last.children || []), child];
      }
    } else {
      const t = raw.trim();
      if (t) out.push(t);
    }
  }
  return out;
}

function RecipientPicker({ label, selected, members, onAdd, onRemove, memberLabel }) {
  const available = (members || []).filter(
    (m) => !selected.includes(String(m.email).toLowerCase())
  );
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, minHeight: 28 }}>
        {selected.length
          ? selected.map((email) => (
            <span
              key={email}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', borderRadius: 999, background: '#f0fdfa',
                border: '1px solid #99f6e4', fontSize: 12, color: '#0f766e',
              }}
            >
              {memberLabel(email)}
              <button
                type="button"
                onClick={() => onRemove(email)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0f766e', padding: 0, fontSize: 14, lineHeight: 1 }}
                aria-label={`Remove ${email}`}
              >
                ×
              </button>
            </span>
          ))
          : <span style={{ fontSize: 12, color: '#94a3b8' }}>None</span>}
      </div>
      <select
        value=""
        onChange={(e) => {
          if (e.target.value) onAdd(e.target.value);
        }}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
      >
        <option value="">
          {available.length ? `Add project member (${available.length} remaining)…` : 'All members already selected'}
        </option>
        {available.map((m) => (
          <option key={m.email} value={String(m.email).toLowerCase()}>
            {m.name || m.email} ({m.email})
          </option>
        ))}
      </select>
    </div>
  );
}

function SectionBlock({ section, editing, onChange, showDiff }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {editing ? (
        <input
          value={section.title}
          onChange={(e) => onChange({ ...section, title: e.target.value, bullets: section.bullets })}
          style={{ fontWeight: 800, fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', width: '100%', marginBottom: 4, boxSizing: 'border-box' }}
        />
      ) : (
        <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', marginBottom: 2 }}>• {section.title}</div>
      )}
      <BulletList
        items={section.bullets || []}
        editing={editing}
        showDiff={showDiff}
        onChange={(bullets) => onChange({ ...section, bullets })}
        compact
      />
    </div>
  );
}

function DiffItem({ item, showDiff, nested }) {
  const textVal = bulletPlain(item);
  const flag = typeof item === 'object' ? item.flag : 'same';
  if (!textVal) return null;
  const style = { marginBottom: 3 };
  if (showDiff && flag === 'new') {
    style.color = '#dc2626';
    style.fontWeight = 600;
  }
  if (showDiff && flag === 'removed') {
    style.textDecoration = 'line-through';
    style.color = '#94a3b8';
  }
  const kids = bulletChildren(item);
  return (
    <li style={style}>
      {textVal}
      {kids.length > 0 && (
        <ul style={{
          margin: '4px 0 0',
          paddingLeft: 18,
          listStyleType: nested ? 'disc' : 'square',
          fontWeight: 400,
        }}
        >
          {kids.map((c, i) => <DiffItem key={i} item={c} showDiff={showDiff} nested />)}
        </ul>
      )}
    </li>
  );
}

function BulletList({ title, items, editing, onChange, compact, showDiff }) {
  const canonical = bulletsToEditText(items);
  const [draft, setDraft] = React.useState(canonical);
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (!focusedRef.current) setDraft(canonical);
  }, [canonical]);

  const rows = compact
    ? Math.min(18, Math.max(5, (draft || canonical).split('\n').length + 1))
    : 4;
  return (
    <div style={compact ? { marginBottom: 4 } : { marginTop: 12 }}>
      {title && <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>• {title}</div>}
      {editing ? (
        <textarea
          value={draft}
          onFocus={() => { focusedRef.current = true; }}
          onChange={(e) => {
            const v = e.target.value;
            setDraft(v);
            onChange(editTextToBullets(v));
          }}
          onBlur={() => {
            focusedRef.current = false;
            const parsed = editTextToBullets(draft);
            onChange(parsed);
            setDraft(bulletsToEditText(parsed));
          }}
          onKeyDown={(e) => e.stopPropagation()}
          rows={rows}
          placeholder="One bullet per line. Indent with two spaces to nest under the line above."
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, boxSizing: 'border-box', whiteSpace: 'pre-wrap' }}
        />
      ) : (
        <ul style={{ margin: 0, paddingLeft: 22, fontSize: 12.5, lineHeight: 1.45, color: '#0f172a', listStyleType: 'circle' }}>
          {(items || []).length
            ? items.map((b, i) => <DiffItem key={i} item={b} showDiff={showDiff} />)
            : <li style={{ color: '#94a3b8' }}>—</li>}
        </ul>
      )}
    </div>
  );
}

function cloneWeek(w) {
  if (!w) return null;
  return JSON.parse(JSON.stringify(w));
}

function defaultSections() {
  return ['Engineering', 'Procurement', 'Construction', 'Interfaces', 'Change Management']
    .map((title) => ({ title, bullets: [] }));
}

const btnSecondary = {
  padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff',
  cursor: 'pointer', fontSize: 13, color: '#334155',
};
const btnPrimary = {
  padding: '7px 14px', borderRadius: 8, border: 'none', background: '#0f766e',
  color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 13,
};

export default AreasOfConcernOnePager;
